import { SocialLoginBusiness, SocialLoginPage } from 'types/SocialLogin';

/**
 * The Safe Connect picker is one list: every portfolio's pages (each row
 * carries its route portfolio in `business_id`), then the pages outside every
 * portfolio. It reads as partial when the poll flagged it at the top level or
 * for any one portfolio.
 */
export const toOboPicker = ({
  businesses,
  outsidePages,
  partial,
}: {
  businesses: SocialLoginBusiness[] | undefined;
  outsidePages: SocialLoginPage[];
  partial: boolean;
}): { pages: SocialLoginPage[]; isPartial: boolean } => ({
  pages: [
    ...(businesses ?? []).flatMap((business) => business.pages),
    ...outsidePages,
  ],
  isPartial:
    partial || (businesses ?? []).some((business) => !!business.partial),
});

/**
 * The Safe Connect picker applies when the poll returned any portfolio or any
 * page outside every portfolio: someone whose only pages are outside still
 * sees them, each with the note on how to connect it.
 */
export const hasOboPicker = ({
  businesses,
  outsidePages,
}: {
  businesses: SocialLoginBusiness[] | undefined;
  outsidePages: SocialLoginPage[];
}): boolean => !!businesses?.length || outsidePages.length > 0;

/**
 * A finished Safe Connect poll with nothing to pick from: no portfolio and no
 * page outside every portfolio. `businesses` is an array only for a Safe
 * Connect poll that is done.
 */
export const isOboEmptyResult = ({
  businesses,
  outsidePages,
}: {
  businesses: SocialLoginBusiness[] | undefined;
  outsidePages: SocialLoginPage[];
}): boolean =>
  Array.isArray(businesses) && !hasOboPicker({ businesses, outsidePages });

/**
 * Nothing to pick from: no portfolio, no page outside every portfolio and
 * (classic flow) no page. Onboarding treats this as a failed connect, so an
 * outside-only result must not count as empty.
 */
export const hasNoPickerResult = ({
  businesses,
  outsidePages,
  pages,
}: {
  businesses: SocialLoginBusiness[] | undefined;
  outsidePages: SocialLoginPage[];
  pages: SocialLoginPage[] | undefined;
}): boolean => !hasOboPicker({ businesses, outsidePages }) && !pages?.length;
